/**
 * 表单初始化 Hook
 *
 * 职责：
 * 1. 编辑模式：回显 defaultData 到 antd Form
 * 2. 新增模式：设置默认值
 * 3. 限速类型：额外请求车道中心线与限速信息
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import { FormInstance } from 'antd/es/form';
import { AnchorLayerType, EffectDateType, EffectVehicleType } from '@/utils/constant';
import { formateTimeLimit } from '@/utils';
import { mapEditorFetch } from '../utils/fetch';
import { HttpStatusCode } from '@/fetch/core/constant';
import { normalizeVehicleLimit } from '../utils/vehicleLimit';
import { getDefaultEffectiveMapList } from '../utils/effectiveMap';

/** 默认全选星期 */
const ALL_WEEK_DAYS = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY',
  'FRIDAY', 'SATURDAY', 'SUNDAY',
];

/** 默认分时段条目 */
const DEFAULT_TIME_LIMIT_ITEM = {
  id: new Date().getTime(),
  value: null,
  errorMes: '请设置每天生效时间',
};

/** 表单默认值 */
export const DEFAULT_FORM_VALUE = {
  type: AnchorLayerType.MOVE_ROUND,
  effectDateType: EffectDateType.PERMANENT,
  effectVehicleType: EffectVehicleType.ALL_EFFECT,
  timeLimit: [DEFAULT_TIME_LIMIT_ITEM],
  remark: '',
  effectTimeType: null,
  vehicleLimit: undefined,
  weekLimit: ALL_WEEK_DAYS,
  status: 'RS_INUSE',
  laneIdList: null,
  speedLimit: null,
  effectiveMapList: undefined,
};

export { ALL_WEEK_DAYS, DEFAULT_TIME_LIMIT_ITEM };

interface UseFormInitOptions {
  form: FormInstance;
  defaultData?: any;
  getLaneCoordinates: (needLane: boolean) => Promise<any>;
}

export const useFormInit = ({ form, defaultData, getLaneCoordinates }: UseFormInitOptions) => {
  const isInitializedRef = useRef(false);
  const lastDataIdRef = useRef(defaultData?.id);
  const [isLaneLoading, setIsLaneLoading] = useState(false);

  /** 加载限速类型详情 */
  const loadSpeedLimitInfo = useCallback(async (data: any) => {
    setIsLaneLoading(true);
    try {
      const res = await mapEditorFetch.getSpeedLimitInfo(data.id);
      if (res.code === HttpStatusCode.Success && res.data) {
        form.setFieldsValue({
          type: data.type,
          remark: data.remark,
          effectDateType: EffectDateType.PERMANENT,
          effectVehicleType: EffectVehicleType.ALL_EFFECT,
          vehicleLimit: undefined,
          laneIdList: res.data.laneIdList,
          speedLimit: res.data.speedLimit,
          effectiveMapList: undefined,
        });
      }
    } finally {
      setIsLaneLoading(false);
    }
  }, [form]);

  /** 初始化表单 */
  useEffect(() => {
    // 编辑对象变化时重置初始化标记，避免切换编辑不同记录时表单不刷新
    if (defaultData?.id !== lastDataIdRef.current) {
      isInitializedRef.current = false;
      lastDataIdRef.current = defaultData?.id;
    }

    if (isInitializedRef.current) return;
    if (!defaultData) {
      form.resetFields();
      form.setFieldsValue(DEFAULT_FORM_VALUE);
      isInitializedRef.current = true;
      return;
    }

    // 限速类型：请求车道中心线与限速信息
    if (defaultData.type === AnchorLayerType.SPEED_LIMIT) {
      loadSpeedLimitInfo(defaultData);
      isInitializedRef.current = true;
      return;
    }

    // 普通类型：回显 defaultData
    const isNavigation = defaultData.type === AnchorLayerType.NAVIGATION;
    const hasFixedVehicleScope = [
      AnchorLayerType.ATTRIBUTE_ANNOTATION,
      AnchorLayerType.LD_ASSIST,
      AnchorLayerType.NAVIGATION,
      AnchorLayerType.NO_HORN_ZONE,
    ].includes(defaultData.type);

    const weekLimit =
      defaultData.effectDateType === 'CYCLICAL_TERM' && defaultData.weekLimit === undefined
        ? ALL_WEEK_DAYS
        : defaultData.weekLimit;

    const effectVehicleType = hasFixedVehicleScope
      ? EffectVehicleType.ALL_EFFECT
      : defaultData.effectVehicleType;

    form.setFieldsValue({
      type: defaultData.type,
      effectDateType: isNavigation
        ? EffectDateType.PERMANENT
        : defaultData.effectDateType,
      timeLimit: formateTimeLimit(defaultData.timeLimit),
      remark: defaultData.remark,
      effectVehicleType,
      vehicleLimit: effectVehicleType === EffectVehicleType.ALL_EFFECT
        ? undefined
        : normalizeVehicleLimit(defaultData.vehicleLimit),
      weekLimit,
      effectTimeType: defaultData.effectTimeType,
      status: defaultData.status,
      subType: defaultData.subType,
      speedLimit: defaultData.speedLimit,
      laneIdList: defaultData.laneIdList,
      topology: defaultData.topology,
      direction: defaultData.direction,
      effectiveMapList: defaultData.type === AnchorLayerType.ROAD_CLOSED
        ? defaultData.effectiveMapList?.length
          ? defaultData.effectiveMapList
          : getDefaultEffectiveMapList()
        : undefined,
    });

    isInitializedRef.current = true;
  }, [defaultData, form, loadSpeedLimitInfo]);

  /** 获取车道中心线信息（限速类型切换时调用） */
  const fetchLaneInfo = useCallback(async (type: AnchorLayerType) => {
    if (type !== AnchorLayerType.SPEED_LIMIT) return;

    const { message } = await import('antd');
    setIsLaneLoading(true);
    try {
      const laneId = await getLaneCoordinates(true);

      if (!laneId) {
        message.error('获取车道中心线失败，请重试');
        form.setFieldsValue({ laneIdList: null, speedLimit: null });
        return;
      }

      if (laneId.length <= 0) {
        message.error('区域内未覆盖道路中心线，请重新绘制');
        form.setFieldsValue({ laneIdList: null, speedLimit: null });
        return;
      }

      form.setFieldsValue({ laneIdList: laneId });
    } finally {
      setIsLaneLoading(false);
    }
  }, [form, getLaneCoordinates]);

  return { fetchLaneInfo, isLaneLoading };
};
